import { useParams } from 'react-router-dom'
import { vehicleChargersCollection } from '@/reducers/vehicleChargers'
import { chargerActiveChargeSettingsCollection } from '@/reducers/chargerActiveChargeSettings'
import { unenrollmentChargerDisplayName } from '@/app/pages/utility-program/unenroll/utils'
import { scheduleToGoalsDescriptions } from '@/app/pages/utility-program/unenroll/suggestions/components/utils/chargeScheduleUtils'
import { ChargerActiveChargeSettings } from '@/types/activeChargeSettings'
import { ChargeSchedule } from '@/types/chargeSchedule'

export type PrescribedGoal = {
  label: string
  requiredDirection: 'on' | 'off'
}

// Solar Surplus is intentionally omitted: it isn't surfaced in the
// configure-devices UI today, so we shouldn't render alerts for it.
const PRESCRIBED_GOAL_LABELS: Array<{
  key: keyof Pick<
    ChargerActiveChargeSettings,
    'money_saving_plan_enabled' | 'save_the_planet_plan_enabled'
  >
  label: string
}> = [
  { key: 'money_saving_plan_enabled', label: 'Save Money' },
  { key: 'save_the_planet_plan_enabled', label: 'Save the Planet' },
]

export function getPrescribedGoals(
  settings: ChargerActiveChargeSettings | undefined,
): PrescribedGoal[] {
  if (!settings) {
    return []
  }
  return PRESCRIBED_GOAL_LABELS.filter(
    ({ key }) => settings[key].is_program_prescribed,
  ).map(({ key, label }) => ({
    label,
    requiredDirection: settings[key].value ? 'on' : 'off',
  }))
}

// Hidden in configure-devices UI — see PRESCRIBED_GOAL_LABELS above.
const HIDDEN_GOAL_LABELS = new Set(['Solar Surplus'])

export function getGoalsValueText(
  settings: ChargerActiveChargeSettings | undefined,
): string {
  const adapter: Pick<
    ChargeSchedule,
    | 'money_saving_plan_enabled'
    | 'save_the_planet_plan_enabled'
    | 'solar_surplus_plan_enabled'
  > = {
    money_saving_plan_enabled:
      settings?.money_saving_plan_enabled.value ?? false,
    save_the_planet_plan_enabled:
      settings?.save_the_planet_plan_enabled.value ?? false,
    solar_surplus_plan_enabled:
      settings?.solar_surplus_plan_enabled.value ?? false,
  }
  const { enabledPlans, disabledPlans } = scheduleToGoalsDescriptions(
    adapter as ChargeSchedule,
  )
  return [
    ...enabledPlans
      .filter((p) => !HIDDEN_GOAL_LABELS.has(p))
      .map((p) => `${p}: Enabled`),
    ...disabledPlans
      .filter((p) => !HIDDEN_GOAL_LABELS.has(p))
      .map((p) => `${p}: Disabled`),
  ].join(', ')
}

export default function useConfigureChargerViewModel() {
  const { deviceId } = useParams<{ deviceId: string }>()
  const chargerId = Number(deviceId)
  const { data: chargers, isLoadingOrIdle: chargersLoading } =
    vehicleChargersCollection.useFetch()
  const charger = chargers?.find((c) => c.id === chargerId)
  const { data: settingsArray, isLoadingOrIdle: settingsLoading } =
    chargerActiveChargeSettingsCollection.useFetch(deviceId)
  const settings = settingsArray[0] as ChargerActiveChargeSettings | undefined

  const isLoadingOrIdle = chargersLoading || settingsLoading || !charger

  const prescribedGoals = getPrescribedGoals(settings)
  const hasPrescribedGoal = prescribedGoals.length > 0

  const displayName = charger ? unenrollmentChargerDisplayName(charger) : ''
  const goalsValueText = getGoalsValueText(settings)

  const settingPaths = {
    chargingPreferences: `/devices/vehicle-charger/${chargerId}`,
  }

  return {
    isLoadingOrIdle,
    charger,
    displayName,
    goalsValueText,
    hasPrescribedGoal,
    prescribedGoals,
    settingPaths,
  }
}
